import { z } from "zod";

import {
  BILLBOARD_SLOT_IDS,
  type BillboardApi,
  type BillboardCreativeInput,
  type BillboardQuote,
  type BillboardReportInput,
  type BillboardSlotsResponse,
  type BillboardStatusResponse,
} from "./BillboardApi";

const placementSchema = z.object({
  id: z.string(),
  message: z.string(),
  destinationUrl: z.string().nullable(),
  activatedAt: z.string().nullable(),
});

const slotSchema = z.object({
  id: z.enum(BILLBOARD_SLOT_IDS),
  anchorPriceCents: z.number().int().min(100),
  anchorAt: z.iso.datetime({ offset: true }),
  currentPriceCents: z.number().int().min(100),
  version: z.number().int().positive(),
  placement: placementSchema.nullable(),
});

const slotsResponseSchema = z.object({
  enabled: z.boolean(),
  serverTime: z.iso.datetime({ offset: true }),
  slots: z.array(slotSchema),
}) satisfies z.ZodType<BillboardSlotsResponse>;

const quoteSchema = z.object({
  placementId: z.string(),
  clientSecret: z.string().min(1),
  amountCents: z.number().int().min(100),
  currency: z.string().length(3),
  slotVersion: z.number().int().positive(),
  quoteExpiresAt: z.iso.datetime({ offset: true }),
}) satisfies z.ZodType<BillboardQuote>;

const statusSchema = z.object({
  placementId: z.string(),
  slotId: z.enum(BILLBOARD_SLOT_IDS),
  status: z.enum([
    "quoted",
    "reserved",
    "active",
    "replaced",
    "failed",
    "canceled",
    "refunded",
    "disputed",
    "voided",
  ]),
  paymentStatus: z.string().nullable(),
  amountCents: z.number().int().min(100),
  currency: z.string().length(3),
  quoteExpiresAt: z.iso.datetime({ offset: true }),
  activatedAt: z.string().nullable(),
  failureReason: z.string().nullable(),
}) satisfies z.ZodType<BillboardStatusResponse>;

const reportResponseSchema = z.object({ accepted: z.boolean() });

export class BillboardHttpError extends Error {
  public constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

export class NetlifyBillboardApi implements BillboardApi {
  public async getSlots(): Promise<BillboardSlotsResponse> {
    return slotsResponseSchema.parse(await this.request("/api/billboards"));
  }

  public async createPaymentIntent(
    input: BillboardCreativeInput,
  ): Promise<BillboardQuote> {
    return quoteSchema.parse(
      await this.request("/api/billboards/payment-intent", {
        method: "POST",
        body: input,
      }),
    );
  }

  public async getStatus(
    placementId: string,
  ): Promise<BillboardStatusResponse> {
    const query = new URLSearchParams({ placement_id: placementId });
    return statusSchema.parse(
      await this.request(`/api/billboards/status?${query}`),
    );
  }

  public async report(
    input: BillboardReportInput,
  ): Promise<{ accepted: boolean }> {
    return reportResponseSchema.parse(
      await this.request("/api/billboards/report", {
        method: "POST",
        body: input,
      }),
    );
  }

  private async request(
    path: string,
    options: { method?: "POST"; body?: unknown } = {},
  ): Promise<unknown> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8_000);
    try {
      const response = await fetch(path, {
        method: options.method ?? "GET",
        credentials: "same-origin",
        ...(options.body
          ? {
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(options.body),
            }
          : {}),
        signal: controller.signal,
      });
      const value: unknown = await response.json().catch(() => null);
      if (!response.ok) {
        const message =
          typeof value === "object" &&
          value !== null &&
          "error" in value &&
          typeof value.error === "string"
            ? value.error
            : "The billboard service is unavailable.";
        throw new BillboardHttpError(response.status, message);
      }
      return value;
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
